/**
 * Structural validation of a condition tree for one use (engine.md §7.1, §8.3). Pure: the limits
 * (`query.max_nodes`, `query.max_depth` in engine_config) are passed in by the caller.
 *
 *   view     every field but script; root may be empty (= everything); nested groups non-empty; conditions complete
 *   filter   source, source-type, title, author, body, tag, read, image, hours, script; root must not be empty
 *   scoring  filter fields minus hours, plus best-score, topic-score, topic; empty root = "otherwise"
 *   model    a model's run rule (models.md §8.2): folder, source, source-type, title, author, body, tag, read,
 *            image, script; empty root = always. No scores, topics or dwell: a model does not wait for another.
 * Score conditions may name a `model` (a model id: lower-case letters, digits and '-').
 *
 * Issue paths are dot paths relative to the tree root (prefix them, e.g. 'condition.'), e.g.
 * 'children.0.value'.
 */

import type { ValidationIssue } from '../contracts/errors.js';
import type { ConditionField, QueryUse } from '../contracts/rules.js';
import { isCompleteCondition } from './matcher.js';

export interface QueryLimits {
  maxNodes: number;
  maxDepth: number;
}

const ALL_FIELDS: readonly ConditionField[] = [
  'folder', 'source', 'source-type', 'topic', 'tag', 'title', 'author', 'body', 'read', 'image', 'topic-score', 'best-score', 'hours', 'script',
];
/** `script` has no SQL form in this round (no stored column), so a View cannot use it (models.md §8.2). */
const VIEW_FIELDS: readonly ConditionField[] = ALL_FIELDS.filter((f) => f !== 'script');
const FILTER_FIELDS: readonly ConditionField[] = ['source', 'source-type', 'title', 'author', 'body', 'tag', 'read', 'image', 'hours', 'script'];
const SCORING_FIELDS: readonly ConditionField[] = [
  'source', 'source-type', 'title', 'author', 'body', 'tag', 'read', 'image', 'best-score', 'topic-score', 'topic', 'script',
];
const MODEL_FIELDS: readonly ConditionField[] = ['folder', 'source', 'source-type', 'title', 'author', 'body', 'tag', 'read', 'image', 'script'];

export const ALLOWED_FIELDS: Readonly<Record<QueryUse, readonly ConditionField[]>> = {
  view: VIEW_FIELDS,
  filter: FILTER_FIELDS,
  scoring: SCORING_FIELDS,
  model: MODEL_FIELDS,
};

const USE_LABEL: Readonly<Record<QueryUse, string>> = { view: 'view', filter: 'filter', scoring: 'scoring rule', model: "model's run rule" };
const MODEL_ID = /^[a-z0-9][a-z0-9-]*$/;

const SET_OPS = ['in', 'not-in'];
const MULTI_OPS = ['in', 'not-in', 'all'];
const TEXT_OPS = ['contains', 'not-contains', 'equals', 'not-equals', 'starts-with', 'ends-with'];
const PHRASE_OPS = ['contains', 'not-contains'];
const NUMBER_OPS = ['gte', 'gt', 'lte', 'lt', 'eq'];

const OPERATORS: Record<ConditionField, readonly string[]> = {
  folder: SET_OPS, source: SET_OPS, 'source-type': SET_OPS, topic: MULTI_OPS, tag: MULTI_OPS,
  title: TEXT_OPS, author: TEXT_OPS, body: PHRASE_OPS, read: ['is'], image: ['is'],
  'topic-score': NUMBER_OPS, 'best-score': NUMBER_OPS, hours: NUMBER_OPS, script: SET_OPS,
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function join(path: string, key: string | number): string {
  return path === '' ? String(key) : `${path}.${key}`;
}

/** Validate `tree` (unknown input) for `use`. An empty array means valid. */
export function validateQuery(tree: unknown, use: QueryUse, limits: QueryLimits): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  let nodes = 0;
  let depthReported = false;
  const allowed = ALLOWED_FIELDS[use];

  const visit = (node: unknown, path: string, depth: number, root: boolean): void => {
    nodes++;
    if (depth > limits.maxDepth && !depthReported) {
      depthReported = true;
      issues.push({ path, message: `Nested deeper than ${limits.maxDepth} levels` });
    }
    if (!isRecord(node)) {
      issues.push({ path, message: 'Expected a group or a condition' });
      return;
    }
    if (node.type === 'group') {
      if (node.operator !== 'all' && node.operator !== 'any') issues.push({ path: join(path, 'operator'), message: 'Operator must be all or any' });
      if (!Array.isArray(node.children)) {
        issues.push({ path: join(path, 'children'), message: 'Children must be a list' });
        return;
      }
      if (node.children.length === 0) {
        if (!root) issues.push({ path: join(path, 'children'), message: 'A group needs at least one condition' });
        else if (use === 'filter') issues.push({ path: join(path, 'children'), message: 'A filter needs at least one condition' });
      }
      node.children.forEach((child: unknown, i: number) => visit(child, join(join(path, 'children'), i), depth + 1, false));
      return;
    }
    if (node.type !== 'condition') {
      issues.push({ path: join(path, 'type'), message: 'Expected a group or a condition' });
      return;
    }
    if (root) issues.push({ path, message: 'The root must be a group' });
    const field = node.field as ConditionField;
    if (!ALL_FIELDS.includes(field)) {
      issues.push({ path: join(path, 'field'), message: `Unknown field ${String(node.field)}` });
      return;
    }
    if (!allowed.includes(field)) {
      issues.push({ path: join(path, 'field'), message: `The field ${field} is not allowed in a ${USE_LABEL[use]}` });
    }
    if (typeof node.operator !== 'string' || !OPERATORS[field].includes(node.operator)) {
      issues.push({ path: join(path, 'operator'), message: `Operator ${String(node.operator)} is not valid for ${field}` });
      return;
    }
    const valueKey = ['folder', 'source', 'source-type', 'topic', 'tag', 'script'].includes(field) ? 'values' : 'value';
    const shapeOk = valueKey === 'values'
      ? Array.isArray(node.values) && node.values.every((v) => typeof v === 'string')
      : ['topic-score', 'best-score', 'hours'].includes(field) ? typeof node.value === 'number' : typeof node.value === 'string';
    if (!shapeOk) {
      issues.push({ path: join(path, valueKey), message: 'The value has the wrong type' });
      return;
    }
    if (field === 'hours' && node.unit !== 'hours' && node.unit !== 'days') {
      issues.push({ path: join(path, 'unit'), message: 'Unit must be hours or days' });
      return;
    }
    if (field === 'topic-score' && node.topic !== undefined && typeof node.topic !== 'string') {
      issues.push({ path: join(path, 'topic'), message: 'Topic must be a topic id' });
      return;
    }
    if ((field === 'topic-score' || field === 'best-score') && node.model !== undefined && (typeof node.model !== 'string' || !MODEL_ID.test(node.model))) {
      issues.push({ path: join(path, 'model'), message: 'Model must be a model id' });
      return;
    }
    if (field === 'script' && Array.isArray(node.values) && !node.values.every((v) => v === 'latin' || v === 'cyrillic' || v === 'other')) {
      issues.push({ path: join(path, 'values'), message: 'A writing system is latin, cyrillic or other' });
      return;
    }
    // The shape is now a ConditionNode.
    if (!isCompleteCondition(node as unknown as Parameters<typeof isCompleteCondition>[0])) {
      issues.push({ path: join(path, valueKey), message: 'The condition is incomplete' });
    }
  };

  visit(tree, '', 1, true);
  if (nodes > limits.maxNodes) issues.push({ path: '', message: `More than ${limits.maxNodes} conditions and groups` });
  return issues;
}
